import { z } from '@bonanza-social/toolkit/api';

export const AccountStatusSchema = z.enum([
  'Pending',
  'Approved',
  'Suspended',
  'Timeout',
  'SelfExclusion',
  'Closed',
]);

export const AccountStatusReasonSchema = z.enum([
  'KYC Address Mismatch',
  'KYC incomplete',
  'KYC in progress',
  'KYC Verification Pending',
  'Document Verification Failed',
  'Manual Verification Required',
  'Within AML Assessment',
  'Security Review Required',
  'Under Security Review',
  'Restricted for Security Review',
  'Restricted - Manual Verification Required',
  'Restricted - Document Verification Failed',
  'Under Security Review - Manual Verification Required',
  'Under Security Review - Document Verification Failed',
  'Under Security Review - KYC in progress',
  'Restricted - KYC in progress',
  'Pending Document Verification Review',
]);

export const AccountVerificationMetaSchema = z.object({
  autoKycAttemptsLeft: z.number(),
  maximumAutoKYCAttempts: z.number(),
});

export const SourceOfFundsFormJsonSchema = z.object({
  question: z.string(),
  questionId: z.string(),
  answers: z.array(
    z.object({
      answer: z.string(),
      additionalAnswer: z.string().optional(),
      controlId: z.string(),
    }),
  ),
});

export const RgQuestionnaireFormJsonSchema = z.object({
  Id: z.number(),
  Text: z.string(),
  Answer: z.boolean().or(z.null()),
});

export type SourceOfFundsFormJson = z.infer<typeof SourceOfFundsFormJsonSchema>;

export const SourceOfFundsFormSchema = z.object({
  id: z.number(),
  accountId: z.number(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  isCompleted: z.boolean(),
  formJson: z.array(SourceOfFundsFormJsonSchema),
  status: z.enum(['Pending', 'Started', 'Completed']),
});

export const RgQuestionnaireFormSchema = z.object({
  id: z.number(),
  accountId: z.number(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  isCompleted: z.boolean(),
  isMandatory: z.boolean(),
  formJson: z.array(RgQuestionnaireFormJsonSchema),
  status: z.enum(['Pending', 'Started', 'Completed']).or(z.string()),
});

export type SourceOfFundsForm = z.infer<typeof SourceOfFundsFormSchema>;

export const MarketingPreferencesSchema = z.object({
  allowSMSMarketingTool: z.boolean(),
  allowEmailMarketingTool: z.boolean(),
  allowInboxMarketingTool: z.boolean().optional(),
  allowBigWinNotifications: z.boolean().optional(),
});

export const EmployerSchema = z.object({
  name: z.string().optional(),
  addressLine1: z.string().optional(),
  phoneNumber: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  phoneExtension: z.string().optional(),
});

export const PersonalDetailsSchema = z
  .object({
    countryOfResidence: z.string(),
    citizenship: z.string(),
    alias: z.string(),
    employer: EmployerSchema,
    firstName: z.string(),
    middleName: z.string(),
    lastName: z.string(),
    gender: z.string(),
    occupation: z.string(),
    industry: z.string(),
    addressLine1: z.string(),
    city: z.string(),
    province: z.string(),
    provinceCode: z.string(),
    country: z.string(),
    countryCode: z.string(),
    postalCode: z.string(),
    phoneCode: z.string(),
    phoneNumber: z.string(),
    birthDate: z.string(),
    screenName: z.string(),
  })
  .partial();

export const UpdatePersonalDetailsRequestSchema = z.object({
  personalDetails: PersonalDetailsSchema.pick({
    countryOfResidence: true,
    citizenship: true,
    alias: true,
  }),
});

export const UpdateEmployerRequestSchema = z.object({
  employer: EmployerSchema,
  isPlayerInitiated: z.boolean(),
});

export const CloseAccountRequestSchema = z.object({
  reason: z.string(),
});

export const MarketingMetaSchema = z.object({
  gclid: z.string().optional(),
  adfcd: z.string().optional(),
  fbclid: z.string().optional(),
  msclkid: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
  utm_variant: z.string().optional(),
  referral_code: z.string().optional(),
  btag: z.string().optional(),
  s1: z.string().optional(),
  t1: z.string().optional(),
  lpageid: z.string().optional(),
  ad_group: z.string().optional(),
  adset: z.string().optional(),
  creative: z.string().optional(),
  keyword: z.string().optional(),
  network: z.string().optional(),
  wbraid: z.string().optional(),
  gbraid: z.string().optional(),
  ttclid: z.string().optional(),
  dclid: z.string().optional(),
  rtclickid: z.string().optional(),
});

export const RegistrationMetaSchema = z.object({
  marketingMeta: MarketingMetaSchema,
});

export const AccountSchema = z.object({
  accountId: z.number(),
  email: z.string(),
  status: AccountStatusSchema,
  statusReason: AccountStatusReasonSchema.optional(),
  isTestAccount: z.boolean(),
  mode: z.boolean().default(true),
  isMkodoWhitelisted: z.boolean(),
  hasMFAEnabled: z.boolean(),
  isPepSettedByPlayer: z.boolean(),
  liveChatGroupId: z.number().optional(),
  liveChatCustomerId: z.string().optional(),
  takeABreakEndDate: z.string().optional(),
  personalDetails: PersonalDetailsSchema.optional(),
  verificationMeta: z.optional(AccountVerificationMetaSchema),
  sourceOfFundsForm: z.optional(SourceOfFundsFormSchema),
  rgQuestionnaireForm: z.optional(RgQuestionnaireFormSchema),
  marketingPreferences: MarketingPreferencesSchema.optional(),
  registrationMeta: z.optional(RegistrationMetaSchema),
});

export const UpdateMFARequestSchema = z.object({
  hasMfaEnabled: z.boolean(),
});
